import { queryOptions, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { api } from "@/api/client"
import type { SessionInfo } from "@/api/generated/SessionInfo"
import { useApiMutation } from "@/api/mutation"
import { useTenantStore } from "@/stores/tenant"

export const sessionKeys = {
  all: ["session"] as const,
}

export const sessionQuery = () =>
  queryOptions({
    queryKey: sessionKeys.all,
    queryFn: ({ signal }) => api.get<SessionInfo>("/auth/session", { signal, skipSessionLost: true }),
    staleTime: 60_000,
  })

/**
 * The current session. Only use inside the authenticated layout, where the
 * route loader guarantees the data is present.
 */
export function useSession(): SessionInfo {
  return useSuspenseQuery(sessionQuery()).data
}

export interface LoginInput {
  username: string
  password: string
}

export function useLogin() {
  const qc = useQueryClient()
  return useApiMutation<{ ok: boolean }, LoginInput>({
    mutationFn: (body) => api.post("/auth/login", body, { skipSessionLost: true, tenantId: null }),
    errorToast: false,
    onSuccess: async () => {
      // Start from a clean slate: nothing cached from a previous user may leak.
      qc.clear()
      useTenantStore.getState().setTenantId(null)
      await qc.fetchQuery(sessionQuery())
    },
  })
}

export function useLogout() {
  const qc = useQueryClient()
  const { t } = useTranslation()
  return useApiMutation<void, void>({
    mutationFn: () => api.post("/auth/logout", undefined, { skipSessionLost: true }),
    successToast: () => t("auth.signedOut"),
    onSettled: () => {
      qc.clear()
      useTenantStore.getState().setTenantId(null)
    },
  })
}
